import React, { useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, EyeIcon, PhoneIcon, MailIcon, CalendarIcon, ClockIcon, DownloadIcon, FileSpreadsheetIcon, UserIcon, LockIcon } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Modal } from '../ui/Modal';
import { Badge, getStatusBadgeVariant } from '../ui/Badge';
import { Tabs, TabPanel } from '../ui/Tabs';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Doctor, DoctorSchedule, Gender } from '../../types';
import { formatTime, getStatusName, getDoctorPrefix } from '../../utils/helpers';
import { exportDoctorsToExcel } from '../../utils/excelExport';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../ui/Pagination';
const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
const DAY_NAMES: Record<string, string> = {
  monday: 'Lunes',
  tuesday: 'Martes',
  wednesday: 'Miércoles',
  thursday: 'Jueves',
  friday: 'Viernes',
  saturday: 'Sábado',
  sunday: 'Domingo'
};
export function Doctors() {
  const {
    doctors,
    addDoctor,
    updateDoctor,
    deleteDoctor,
    specialties,
    getSpecialty,
    getAppointmentsByDoctor,
    getPatient,
    getService
  } = useData();
  const {
    user
  } = useAuth();
  const isAdmin = user?.role === 'administrador';
  const {
    canCreate,
    canEdit,
    canDelete,
    isReadOnly
  } = usePermissions();
  const readOnly = isReadOnly('doctors');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSpecialty, setFilterSpecialty] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | null>(null);
  const [activeTab, setActiveTab] = useState('info');
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    gender: 'masculino' as Gender,
    specialtyId: '',
    phone: '',
    email: ''
  });
  const [scheduleData, setScheduleData] = useState<DoctorSchedule>({
    monday: {
      start: '08:00',
      end: '17:00'
    },
    tuesday: {
      start: '08:00',
      end: '17:00'
    },
    wednesday: {
      start: '08:00',
      end: '17:00'
    },
    thursday: {
      start: '08:00',
      end: '17:00'
    },
    friday: {
      start: '08:00',
      end: '14:00'
    },
    saturday: null,
    sunday: null
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const filteredDoctors = doctors.filter(d => {
    const matchesSearch = d.name.toLowerCase().includes(searchTerm.toLowerCase()) || d.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSpecialty = !filterSpecialty || d.specialtyId === filterSpecialty;
    return matchesSearch && matchesSpecialty;
  });
  const {
    paginatedItems: paginatedDoctors,
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    goToPage,
    nextPage,
    prevPage
  } = usePagination(filteredDoctors);
  const specialtyOptions = [{
    value: '',
    label: 'Todas las especialidades'
  }, ...specialties.map(s => ({
    value: s.id,
    label: s.name
  }))];
  const specialtyOptionsForForm = specialties.map(s => ({
    value: s.id,
    label: s.name
  }));
  const openNewForm = () => {
    if (!canCreate('doctors')) return;
    setSelectedDoctor(null);
    setFormData({
      name: '',
      gender: 'masculino',
      specialtyId: '',
      phone: '',
      email: ''
    });
    setScheduleData({
      monday: {
        start: '08:00',
        end: '17:00'
      },
      tuesday: {
        start: '08:00',
        end: '17:00'
      },
      wednesday: {
        start: '08:00',
        end: '17:00'
      },
      thursday: {
        start: '08:00',
        end: '17:00'
      },
      friday: {
        start: '08:00',
        end: '14:00'
      },
      saturday: null,
      sunday: null
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (doctor: Doctor) => {
    if (!canEdit('doctors')) return;
    setSelectedDoctor(doctor);
    setFormData({
      name: doctor.name,
      gender: doctor.gender || 'masculino',
      specialtyId: doctor.specialtyId,
      phone: doctor.phone,
      email: doctor.email
    });
    setScheduleData(doctor.schedule);
    setFormErrors({});
    setIsViewOpen(false);
    setIsFormOpen(true);
  };
  const openView = (doctor: Doctor) => {
    setSelectedDoctor(doctor);
    setActiveTab('info');
    setIsViewOpen(true);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.name) errors.name = 'El nombre es requerido';
    if (!formData.specialtyId) errors.specialtyId = 'La especialidad es requerida';
    if (!formData.phone) errors.phone = 'El teléfono es requerido';
    if (!formData.email) errors.email = 'El correo es requerido';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    if (selectedDoctor) {
      updateDoctor(selectedDoctor.id, {
        ...formData,
        schedule: scheduleData
      });
    } else {
      addDoctor({
        ...formData,
        schedule: scheduleData
      });
    }
    setIsFormOpen(false);
  };
  const handleDelete = () => {
    if (!canDelete('doctors')) return;
    if (selectedDoctor) {
      deleteDoctor(selectedDoctor.id);
      setIsDeleteConfirmOpen(false);
      setIsViewOpen(false);
    }
  };
  const toggleDaySchedule = (day: keyof DoctorSchedule) => {
    setScheduleData(prev => ({
      ...prev,
      [day]: prev[day] ? null : {
        start: '08:00',
        end: '17:00'
      }
    }));
  };
  const updateDaySchedule = (day: keyof DoctorSchedule, field: 'start' | 'end', value: string) => {
    setScheduleData(prev => ({
      ...prev,
      [day]: prev[day] ? {
        ...prev[day]!,
        [field]: value
      } : null
    }));
  };
  const tabs = [{
    id: 'info',
    label: 'Información',
    icon: <ClockIcon className="w-4 h-4" data-id="element-906" />
  }, {
    id: 'schedule',
    label: 'Horario',
    icon: <CalendarIcon className="w-4 h-4" data-id="element-907" />
  }, {
    id: 'appointments',
    label: 'Citas',
    icon: <CalendarIcon className="w-4 h-4" data-id="element-908" />
  }];
  const handleExportToExcel = async () => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportDoctorsToExcel(doctors, (specialtyId: string) => {
      const specialty = getSpecialty(specialtyId);
      return specialty?.name || '-';
    });
    setIsExporting(false);
  };
  return <div className="space-y-6" data-id="element-909">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-910">
        <div data-id="element-911">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-912">
            Doctores
          </h1>
          <p className="text-gray-500 dark:text-gray-400" data-id="element-913">
            Gestión de doctores y especialidades
            {readOnly && <span className="ml-2 text-amber-600 dark:text-amber-400" data-id="element-914">
                (Solo lectura)
              </span>}
          </p>
        </div>
        {canCreate('doctors') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-916" />} onClick={openNewForm} data-id="element-915">
            Nuevo Doctor
          </Button>}
      </div>

      {/* Beautiful Export Card - Only for Admin */}
      {isAdmin && <Card className="bg-gradient-to-br from-sky-50 to-cyan-50 dark:from-sky-950/30 dark:to-cyan-950/30 border-sky-200 dark:border-sky-800" data-id="element-917">
          <div className="flex items-center justify-between" data-id="element-918">
            <div className="flex items-center gap-4" data-id="element-919">
              <div className="w-14 h-14 rounded-xl bg-sky-500 dark:bg-sky-600 flex items-center justify-center shadow-lg shadow-sky-500/20" data-id="element-920">
                <FileSpreadsheetIcon className="w-7 h-7 text-white" data-id="element-921" />
              </div>
              <div data-id="element-922">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1" data-id="element-923">
                  Exportar Base de Datos
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-924">
                  Descarga todos los doctores en formato Excel
                </p>
                <div className="flex items-center gap-4 mt-2" data-id="element-925">
                  <div className="flex items-center gap-1.5 text-sm text-sky-700 dark:text-sky-400" data-id="element-926">
                    <UserIcon className="w-4 h-4" data-id="element-927" />
                    <span className="font-medium" data-id="element-928">
                      {doctors.length} doctores
                    </span>
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-929">
                    • Incluye: Nombre, Especialidad, Contacto, Horarios
                  </div>
                </div>
              </div>
            </div>
            <Button leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-931" />} onClick={handleExportToExcel} isLoading={isExporting} className="shadow-lg shadow-sky-500/20 hover:shadow-xl hover:shadow-sky-500/30 transition-all duration-200" data-id="element-930">
              {isExporting ? 'Exportando...' : 'Descargar Excel'}
            </Button>
          </div>
        </Card>}

      {/* Filters */}
      <Card data-id="element-932">
        <div className="flex items-center gap-4" data-id="element-933">
          <div className="flex-1 relative" data-id="element-934">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-935" />
            <Input placeholder="Buscar por nombre o correo..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-id="element-936" />
          </div>
          <div className="w-64" data-id="element-937">
            <Select options={specialtyOptions} value={filterSpecialty} onChange={setFilterSpecialty} placeholder="Filtrar por especialidad" data-id="element-938" />
          </div>
        </div>
      </Card>

      {readOnly && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-939">
          <LockIcon className="w-4 h-4" data-id="element-940" />
          <span data-id="element-941">
            Solo tienes permisos de lectura. No puedes crear, editar o eliminar
            doctores.
          </span>
        </div>}

      {/* Doctors List */}
      <Card padding="none" data-id="element-942">
        <div className="overflow-x-auto" data-id="element-943">
          <table className="w-full" data-id="element-944">
            <thead data-id="element-945">
              <tr className="border-b border-gray-200 dark:border-gray-700" data-id="element-946">
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-947">
                  Doctor
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-948">
                  Especialidad
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-949">
                  Contacto
                </th>
                <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-950">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-951">
              {filteredDoctors.length === 0 ? <tr data-id="element-952">
                  <td colSpan={4} className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-953">
                    No se encontraron doctores
                  </td>
                </tr> : paginatedDoctors.map(doctor => {
              const specialty = getSpecialty(doctor.specialtyId);
              return <tr key={doctor.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50" data-id="element-954">
                      <td className="py-4 px-6" data-id="element-955">
                        <p className="font-medium text-gray-900 dark:text-white" data-id="element-956">
                          {getDoctorPrefix(doctor.gender)} {doctor.name}
                        </p>
                      </td>
                      <td className="py-4 px-6" data-id="element-957">
                        <Badge variant="primary" data-id="element-958">
                          {specialty?.name || '-'}
                        </Badge>
                      </td>
                      <td className="py-4 px-6" data-id="element-959">
                        <div className="space-y-1" data-id="element-960">
                          <p className="text-sm text-gray-900 dark:text-gray-100 flex items-center gap-1" data-id="element-961">
                            <PhoneIcon className="w-3 h-3" data-id="element-962" /> {doctor.phone}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1" data-id="element-963">
                            <MailIcon className="w-3 h-3" data-id="element-964" /> {doctor.email}
                          </p>
                        </div>
                      </td>
                      <td className="py-4 px-6" data-id="element-965">
                        <div className="flex items-center justify-end gap-2" data-id="element-966">
                          <button onClick={() => openView(doctor)} className="p-2 text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-lg transition-colors" title="Ver detalles" data-id="element-967">
                            <EyeIcon className="w-4 h-4" data-id="element-968" />
                          </button>
                          {canEdit('doctors') ? <button onClick={() => openEditForm(doctor)} className="p-2 text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-lg transition-colors" title="Editar" data-id="element-969">
                              <EditIcon className="w-4 h-4" data-id="element-970" />
                            </button> : <button disabled className="p-2 text-gray-300 dark:text-gray-600 cursor-not-allowed" title="Sin permisos para editar" data-id="element-971">
                              <LockIcon className="w-4 h-4" data-id="element-972" />
                            </button>}
                        </div>
                      </td>
                    </tr>;
            })}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="px-6 pb-4" data-id="element-973">
          <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} onPageChange={goToPage} onNext={nextPage} onPrev={prevPage} data-id="element-974" />
        </div>
      </Card>

      {/* Doctor Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedDoctor ? 'Editar Doctor' : 'Nuevo Doctor'} size="lg" icon={<UserIcon className="w-5 h-5" data-id="element-976" />} accentColor="sky" data-id="element-975">
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-977">
          {/* Personal Info Section */}
          <div data-id="element-978">
            <div className="flex items-center gap-2 mb-4" data-id="element-979">
              <span className="w-1.5 h-5 rounded-full bg-sky-500" data-id="element-980"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-981">
                Información Personal
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-982">
              <div className="grid grid-cols-2 gap-4" data-id="element-983">
                <Input label="Nombre Completo" value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} error={formErrors.name} placeholder="Nombre del doctor" data-id="element-984" />
                <Select label="Género" options={[{
                value: 'masculino',
                label: 'Masculino'
              }, {
                value: 'femenino',
                label: 'Femenino'
              }]} value={formData.gender} onChange={value => setFormData({
                ...formData,
                gender: value as Gender
              })} data-id="element-985" />
              </div>
              <Select label="Especialidad" options={specialtyOptionsForForm} value={formData.specialtyId} onChange={value => setFormData({
              ...formData,
              specialtyId: value
            })} error={formErrors.specialtyId} data-id="element-986" />
            </div>
          </div>

          {/* Contact Section */}
          <div data-id="element-987">
            <div className="flex items-center gap-2 mb-4" data-id="element-988">
              <span className="w-1.5 h-5 rounded-full bg-cyan-500" data-id="element-989"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-990">
                Contacto
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-991">
              <div className="grid grid-cols-2 gap-4" data-id="element-992">
                <Input label="Teléfono" value={formData.phone} onChange={e => setFormData({
                ...formData,
                phone: e.target.value
              })} error={formErrors.phone} placeholder="809-000-0000" data-id="element-993" />
                <Input type="email" label="Correo Electrónico" value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} error={formErrors.email} placeholder="doctor@email.com" data-id="element-994" />
              </div>
            </div>
          </div>

          {/* Schedule Section */}
          <div data-id="element-995">
            <div className="flex items-center gap-2 mb-4" data-id="element-996">
              <span className="w-1.5 h-5 rounded-full bg-teal-500" data-id="element-997"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-998">
                Horario de Atención
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-100 dark:border-gray-700/50" data-id="element-999">
              <div className="space-y-1" data-id="element-1000">
                {DAYS.map(day => {
                const isActive = scheduleData[day] !== null;
                return <div key={day} className={`flex items-center gap-4 p-3 rounded-lg transition-colors ${isActive ? 'bg-white dark:bg-gray-800 border border-sky-100 dark:border-sky-800/30 shadow-sm' : 'border border-transparent'}`} data-id="element-1001">
                      <label className="flex items-center gap-3 w-36 cursor-pointer select-none" data-id="element-1002">
                        <div className="relative" data-id="element-1003">
                          <input type="checkbox" checked={isActive} onChange={() => toggleDaySchedule(day)} className="sr-only peer" data-id="element-1004" />
                          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${isActive ? 'bg-sky-500 border-sky-500 dark:bg-sky-600 dark:border-sky-600' : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500'}`} data-id="element-1005">
                            {isActive && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-1006">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-1007" />
                              </svg>}
                          </div>
                        </div>
                        <span className={`text-sm font-medium ${isActive ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'}`} data-id="element-1008">
                          {DAY_NAMES[day]}
                        </span>
                      </label>
                      {isActive && <div className="flex items-center gap-2 flex-1" data-id="element-1009">
                          <Input type="time" value={scheduleData[day]!.start} onChange={e => updateDaySchedule(day, 'start', e.target.value)} className="w-32" data-id="element-1010" />
                          <span className="text-gray-400 text-sm font-medium" data-id="element-1011">
                            a
                          </span>
                          <Input type="time" value={scheduleData[day]!.end} onChange={e => updateDaySchedule(day, 'end', e.target.value)} className="w-32" data-id="element-1012" />
                        </div>}
                    </div>;
              })}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-1013">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-1014">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-1015">
              {selectedDoctor ? 'Guardar Cambios' : 'Crear Doctor'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Doctor View Modal */}
      <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="Detalles del Doctor" size="lg" data-id="element-1016">
        {selectedDoctor && <div className="space-y-6" data-id="element-1017">
            {/* Hero Header */}
            <div className="relative rounded-xl overflow-hidden bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm" data-id="element-1018">
              <div className="h-20 bg-gradient-to-r from-sky-400 to-cyan-500 dark:from-sky-600 dark:to-cyan-700" data-id="element-1019"></div>
              <div className="px-6 pb-5 pt-0" data-id="element-1020">
                <div className="flex items-end gap-4 -mt-10" data-id="element-1021">
                  <div className="w-20 h-20 rounded-xl bg-white dark:bg-gray-800 p-1 shadow-lg flex-shrink-0" data-id="element-1022">
                    <div className="w-full h-full rounded-lg bg-sky-100 dark:bg-sky-900/50 flex items-center justify-center text-2xl font-bold text-sky-600 dark:text-sky-400" data-id="element-1023">
                      {selectedDoctor.name.charAt(0).toUpperCase()}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0 pb-1" data-id="element-1024">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white truncate" data-id="element-1025">
                      {getDoctorPrefix(selectedDoctor.gender)}{' '}
                      {selectedDoctor.name}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 font-medium" data-id="element-1026">
                      {getSpecialty(selectedDoctor.specialtyId)?.name}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0 pb-1" data-id="element-1027">
                    {!readOnly && <>
                        {canEdit('doctors') && <Button variant="outline" size="sm" onClick={() => openEditForm(selectedDoctor)} data-id="element-1028">
                            <EditIcon className="w-4 h-4 mr-2" data-id="element-1029" /> Editar
                          </Button>}
                        {canDelete('doctors') && <Button variant="danger" size="sm" onClick={() => setIsDeleteConfirmOpen(true)} data-id="element-1030">
                            <TrashIcon className="w-4 h-4" data-id="element-1031" />
                          </Button>}
                      </>}
                  </div>
                </div>
              </div>
            </div>

            <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} data-id="element-1032">
              <TabPanel id="info" activeTab={activeTab} data-id="element-1033">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1034">
                  <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700" data-id="element-1035">
                    <div className="flex items-center gap-3 mb-3" data-id="element-1036">
                      <div className="w-8 h-8 rounded-lg bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center text-sky-600 dark:text-sky-400" data-id="element-1037">
                        <PhoneIcon className="w-4 h-4" data-id="element-1038" />
                      </div>
                      <h4 className="font-semibold text-gray-900 dark:text-white" data-id="element-1039">
                        Teléfono
                      </h4>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 pl-11" data-id="element-1040">
                      {selectedDoctor.phone}
                    </p>
                  </div>

                  <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700" data-id="element-1041">
                    <div className="flex items-center gap-3 mb-3" data-id="element-1042">
                      <div className="w-8 h-8 rounded-lg bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center text-cyan-600 dark:text-cyan-400" data-id="element-1043">
                        <MailIcon className="w-4 h-4" data-id="element-1044" />
                      </div>
                      <h4 className="font-semibold text-gray-900 dark:text-white" data-id="element-1045">
                        Correo Electrónico
                      </h4>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 pl-11" data-id="element-1046">
                      {selectedDoctor.email}
                    </p>
                  </div>

                  <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700" data-id="element-1047">
                    <div className="flex items-center gap-3 mb-3" data-id="element-1048">
                      <div className="w-8 h-8 rounded-lg bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center text-teal-600 dark:text-teal-400" data-id="element-1049">
                        <UserIcon className="w-4 h-4" data-id="element-1050" />
                      </div>
                      <h4 className="font-semibold text-gray-900 dark:text-white" data-id="element-1051">
                        Género
                      </h4>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 pl-11 capitalize" data-id="element-1052">
                      {selectedDoctor.gender || 'No especificado'}
                    </p>
                  </div>
                </div>
              </TabPanel>

              <TabPanel id="schedule" activeTab={activeTab} data-id="element-1053">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1054">
                  {DAYS.map(day => <div key={day} className={`flex items-center justify-between p-3 rounded-lg border ${selectedDoctor.schedule[day] ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700' : 'bg-gray-50 dark:bg-gray-800/50 border-transparent'}`} data-id="element-1055">
                      <span className="font-medium text-gray-900 dark:text-white" data-id="element-1056">
                        {DAY_NAMES[day]}
                      </span>
                      {selectedDoctor.schedule[day] ? <span className="text-sm font-medium text-sky-600 dark:text-sky-400 bg-sky-50 dark:bg-sky-900/20 px-2 py-1 rounded" data-id="element-1057">
                          {formatTime(selectedDoctor.schedule[day]!.start)} -{' '}
                          {formatTime(selectedDoctor.schedule[day]!.end)}
                        </span> : <span className="text-sm text-gray-400 italic" data-id="element-1058">
                          No disponible
                        </span>}
                    </div>)}
                </div>
              </TabPanel>

              <TabPanel id="appointments" activeTab={activeTab} data-id="element-1059">
                <div className="space-y-4" data-id="element-1060">
                  {getAppointmentsByDoctor(selectedDoctor.id).length === 0 ? <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-700" data-id="element-1061">
                      <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-1062" />
                      <p className="text-gray-500 dark:text-gray-400" data-id="element-1063">
                        No hay citas registradas
                      </p>
                    </div> : getAppointmentsByDoctor(selectedDoctor.id).map(apt => {
                const patient = getPatient(apt.patientId);
                const service = getService(apt.serviceId);
                return <div key={apt.id} className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl hover:shadow-sm transition-shadow" data-id="element-1064">
                          <div className="flex items-center gap-4" data-id="element-1065">
                            <div className="w-10 h-10 rounded-full bg-sky-50 dark:bg-sky-900/20 flex items-center justify-center text-sky-600 dark:text-sky-400 font-bold" data-id="element-1066">
                              {patient?.name.charAt(0)}
                            </div>
                            <div data-id="element-1067">
                              <p className="font-medium text-gray-900 dark:text-white" data-id="element-1068">
                                {patient?.name}
                              </p>
                              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400" data-id="element-1069">
                                <span data-id="element-1070">{apt.date}</span>
                                <span data-id="element-1071">•</span>
                                <span data-id="element-1072">{formatTime(apt.time)}</span>
                                <span data-id="element-1073">•</span>
                                <span data-id="element-1074">{service?.name}</span>
                              </div>
                            </div>
                          </div>
                          <Badge variant={getStatusBadgeVariant(apt.status)} data-id="element-1075">
                            {getStatusName(apt.status)}
                          </Badge>
                        </div>;
              })}
                </div>
              </TabPanel>
            </Tabs>
          </div>}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} title="Confirmar Eliminación" size="sm" data-id="element-1076">
        <div className="space-y-4" data-id="element-1077">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-1078">
            ¿Está seguro de que desea eliminar a este doctor? Esta acción no se
            puede deshacer.
          </p>
          <div className="flex justify-end gap-3" data-id="element-1079">
            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)} data-id="element-1080">
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} data-id="element-1081">
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}